import { setTaskTerminalStatuses } from '@/lib/projects/task-schedule';
import { router } from '@inertiajs/react';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// Define types for settings constants based on config.json structure
export interface SettingsConstants {
    countries: Array<{ value: string; label: string }>;
    homeCountry: string;
    navigationVisibility: {
        home: boolean;
        analytics: boolean;
        colleagues: boolean;
        companies: boolean;
        contacts: boolean;
        leads: boolean;
        projects: boolean;
        workOrders: boolean;
        purchaseOrders: boolean;
        knowledgeBase: boolean;
        serviceRequests: boolean;
        vacations?: boolean;
    };
    appLocale: string;
    availableLocales: string[];
    defaultCompanyData: {
        name: string;
        address: string;
        company_code: string;
        vat_number: string;
        phone: string;
        email: string;
    };
    companyCategories: string[];
    companyTypes: string[];
    companyPriorities: Array<{ value: string; label: string; color: string; icon: string }>;
    leadStatuses: Array<{ value: string; label: string; color: string; isTerminal: boolean }>;
    leadPriorities: Array<{ value: string; label: string; color: string; icon: string }>;
    leadSources: string[];
    costLineTypes: string[];
    projectStatuses: Array<{ value: string; label: string; color: string; isTerminal: boolean }>;
    projectTaskStatuses: Array<{ value: string; label: string; color: string; isTerminal: boolean }>;
    projectTaskTypes: Array<{ value: string; label: string; color: string; icon: string }>;
    projectCountryTypes: Array<{ value: string; label: string; color: string; icon: string }>;
    projectTaskTemplates: Array<{
        value: string;
        label: string;
        tasks: Array<{ department_key?: string; type?: string; name: string }>;
    }>;
    projectTaskDepartments: Array<{
        value: string;
        label: string;
        roles: string[];
    }>;
    projectRoleTranslations: Array<{
        value: string;
        label: string;
        label_lt?: string;
        /** Absent means visible — only an explicit `false` hides the role from task department lists. */
        enabled?: boolean;
    }>;
    projectCostLineTypes: string[];
    workOrderStatuses: Array<{ value: string; label: string; color: string; isTerminal: boolean }>;
    workOrderPriorities: Array<{ value: string; label: string; color: string; icon: string }>;
    workOrderTypes: Array<{ value: string; label: string; color: string; icon: string }>;
    workOrderCostLineTypes: string[];
    taskTypes: Array<{ value: string; label: string; color: string; icon: string }>;
    /** Profit margin (%) at or above which a lead offer is approved automatically. */
    leadOfferApprovalThreshold: number;
    appLogo: string | null;
    appFavicon: string | null;
    availableRoles: string[];
}

// Legacy constants for backward compatibility
export interface LegacyConstants {
    LEAD_STATUS_OPTIONS: Array<{ value: string; label: string; color: string; isTerminal: boolean }>;
    LEAD_PRIORITY_OPTIONS: Array<{ value: string; label: string; color: string; icon: string }>;
    COMPANY_PRIORITY_OPTIONS: Array<{ value: string; label: string; color: string; icon: string }>;
    PROJECT_STATUS_OPTIONS: Array<{ value: string; label: string; color: string; isTerminal: boolean }>;
    PROJECT_TASK_STATUS_OPTIONS: Array<{ value: string; label: string; color: string; isTerminal: boolean }>;
    PROJECT_TASK_TYPE_OPTIONS: Array<{ value: string; label: string; color: string; icon: string }>;
    PROJECT_COUNTRY_TYPE_OPTIONS: Array<{ value: string; label: string; color: string; icon: string }>;
    WORK_ORDER_STATUS_OPTIONS: Array<{ value: string; label: string; color: string; isTerminal: boolean }>;
    WORK_ORDER_PRIORITY_OPTIONS: Array<{ value: string; label: string; color: string; icon: string }>;
    WORK_ORDER_TYPE_OPTIONS: Array<{ value: string; label: string; color: string; icon: string }>;
    TASK_TYPE_OPTIONS: Array<{ value: string; label: string; color: string; icon: string }>;
    COUNTRY_OPTIONS: Array<{ value: string; label: string }>;
    COMPANY_CATEGORY_OPTIONS: Array<{ value: string; label: string }>;
    COMPANY_TYPE_OPTIONS: Array<{ value: string; label: string }>;
    LEAD_SOURCE_OPTIONS: Array<{ value: string; label: string }>;
    COST_LINE_TYPE_OPTIONS: Array<{ value: string; label: string }>;
    PROJECT_COST_LINE_TYPE_OPTIONS: Array<{ value: string; label: string }>;
    WORK_ORDER_COST_LINE_TYPE_OPTIONS: Array<{ value: string; label: string }>;
}

interface SettingsContextType {
    settings: SettingsConstants;
    legacy: LegacyConstants;
    isLoading: boolean;
    error: string | null;
    getDefaultValue: (key: string) => unknown;
    getOptions: (key: string) => Array<{ value: string; label: string }>;
    getValueLabelPairs: (key: string) => Array<{ value: string; label: string }>;
}

const fallbackSettings: SettingsConstants = {
    countries: [],
    homeCountry: 'Lithuania',
    navigationVisibility: {
        home: true,
        analytics: true,
        colleagues: true,
        companies: true,
        contacts: true,
        leads: true,
        projects: true,
        workOrders: true,
        purchaseOrders: true,
        knowledgeBase: true,
        serviceRequests: true,
    },
    appLocale: 'en',
    availableLocales: ['en', 'lt'],
    defaultCompanyData: {
        name: '',
        address: '',
        company_code: '',
        vat_number: '',
        phone: '',
        email: '',
    },
    companyCategories: [],
    companyTypes: [],
    companyPriorities: [],
    leadStatuses: [],
    leadPriorities: [],
    leadSources: [],
    costLineTypes: [],
    projectStatuses: [],
    projectTaskStatuses: [],
    projectTaskTypes: [],
    projectCountryTypes: [],
    projectTaskTemplates: [],
    projectTaskDepartments: [],
    projectRoleTranslations: [],
    projectCostLineTypes: [],
    workOrderStatuses: [],
    workOrderPriorities: [],
    workOrderTypes: [],
    workOrderCostLineTypes: [],
    taskTypes: [],
    leadOfferApprovalThreshold: 10,
    appLogo: null,
    appFavicon: null,
    availableRoles: [],
};

const fallbackLegacy: LegacyConstants = {
    LEAD_STATUS_OPTIONS: [],
    LEAD_PRIORITY_OPTIONS: [],
    COMPANY_PRIORITY_OPTIONS: [],
    PROJECT_STATUS_OPTIONS: [],
    PROJECT_TASK_STATUS_OPTIONS: [],
    PROJECT_TASK_TYPE_OPTIONS: [],
    PROJECT_COUNTRY_TYPE_OPTIONS: [],
    WORK_ORDER_STATUS_OPTIONS: [],
    WORK_ORDER_PRIORITY_OPTIONS: [],
    WORK_ORDER_TYPE_OPTIONS: [],
    TASK_TYPE_OPTIONS: [],
    COUNTRY_OPTIONS: [],
    COMPANY_CATEGORY_OPTIONS: [],
    COMPANY_TYPE_OPTIONS: [],
    LEAD_SOURCE_OPTIONS: [],
    COST_LINE_TYPE_OPTIONS: [],
    PROJECT_COST_LINE_TYPE_OPTIONS: [],
    WORK_ORDER_COST_LINE_TYPE_OPTIONS: [],
};

const fallbackContextValue: SettingsContextType = {
    settings: fallbackSettings,
    legacy: fallbackLegacy,
    isLoading: true,
    error: 'SettingsProvider missing',
    getDefaultValue: () => null,
    getOptions: () => [],
    getValueLabelPairs: () => [],
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface AuthPageProps {
    auth?: { user?: { id?: number } | null } | null;
}

function readAuthUserId(props: unknown): number | null {
    return (props as AuthPageProps | undefined)?.auth?.user?.id ?? null;
}

function readInitialAuthUserId(): number | null {
    if (typeof document === 'undefined') {
        return null;
    }

    try {
        const page = document.getElementById('app')?.dataset.page;

        return page ? readAuthUserId((JSON.parse(page) as { props?: unknown }).props) : null;
    } catch {
        return null;
    }
}

/**
 * The provider is mounted outside Inertia's <App>, so usePage() would throw here.
 * Read the auth user from the initial page payload instead and keep it in sync
 * with client-side visits (e.g. logging in) through the router's navigate event.
 */
function useAuthUserId(): number | null {
    const [userId, setUserId] = useState<number | null>(readInitialAuthUserId);

    useEffect(() => router.on('navigate', (event) => setUserId(readAuthUserId(event.detail.page.props))), []);

    return userId;
}

interface SettingsProviderProps {
    children: ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
    const [settings, setSettings] = useState<SettingsConstants>({
        countries: [],
        homeCountry: 'Lithuania',
        navigationVisibility: {
            home: true,
            analytics: true,
            colleagues: true,
            companies: true,
            contacts: true,
            leads: true,
            projects: true,
            workOrders: true,
            purchaseOrders: true,
            knowledgeBase: true,
            serviceRequests: true,
        },
        appLocale: 'en',
        availableLocales: ['en', 'lt'],
        defaultCompanyData: {
            name: '',
            address: '',
            company_code: '',
            vat_number: '',
            phone: '',
            email: '',
        },
        companyCategories: [],
        companyTypes: [],
        companyPriorities: [],
        leadStatuses: [],
        leadPriorities: [],
        leadSources: [],
        costLineTypes: [],
        projectStatuses: [],
        projectTaskStatuses: [],
        projectTaskTypes: [],
        projectCountryTypes: [],
        projectTaskTemplates: [],
        projectTaskDepartments: [],
        projectRoleTranslations: [],
        projectCostLineTypes: [],
        workOrderStatuses: [],
        workOrderPriorities: [],
        workOrderTypes: [],
        workOrderCostLineTypes: [],
        taskTypes: [],
        leadOfferApprovalThreshold: 10,
        appLogo: null,
        appFavicon: null,
        availableRoles: [],
    });

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const authUserId = useAuthUserId();

    useEffect(() => {
        // The constants endpoint is behind auth middleware: on guest pages it
        // redirects to the login HTML, so skip it and use the defaults above.
        if (authUserId === null) {
            setIsLoading(false);
            setError(null);

            return;
        }

        const fetchSettings = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await fetch(route('settings.constants'));
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                if (!response.headers.get('content-type')?.includes('application/json')) {
                    throw new Error(
                        `Expected JSON from ${route('settings.constants')} but got ${response.headers.get('content-type') ?? 'no content type'}.`,
                    );
                }

                const data: SettingsConstants = await response.json();
                setSettings((previous) => ({
                    ...previous,
                    ...data,
                }));

                // The task Schedule filter is evaluated outside React context, so
                // it reads the terminal statuses from a module the provider feeds.
                setTaskTerminalStatuses(data.projectTaskStatuses ?? []);
            } catch (e: unknown) {
                setError(e instanceof Error ? e.message : 'Failed to fetch settings constants.');
                console.error('Failed to fetch settings constants:', e);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, [authUserId]);

    // Create legacy constants for backward compatibility (untranslated base data)
    const legacy: LegacyConstants = {
        LEAD_STATUS_OPTIONS: settings.leadStatuses,
        LEAD_PRIORITY_OPTIONS: settings.leadPriorities,
        COMPANY_PRIORITY_OPTIONS: settings.companyPriorities,
        PROJECT_STATUS_OPTIONS: settings.projectStatuses,
        PROJECT_TASK_STATUS_OPTIONS: settings.projectTaskStatuses,
        PROJECT_TASK_TYPE_OPTIONS: settings.projectTaskTypes,
        PROJECT_COUNTRY_TYPE_OPTIONS: settings.projectCountryTypes,
        WORK_ORDER_STATUS_OPTIONS: settings.workOrderStatuses,
        WORK_ORDER_PRIORITY_OPTIONS: settings.workOrderPriorities,
        WORK_ORDER_TYPE_OPTIONS: settings.workOrderTypes,
        TASK_TYPE_OPTIONS: settings.taskTypes,
        COUNTRY_OPTIONS: settings.countries,
        COMPANY_CATEGORY_OPTIONS: settings.companyCategories.map((category) => ({
            value: category,
            label: category,
        })),
        COMPANY_TYPE_OPTIONS: settings.companyTypes.map((type) => ({
            value: type,
            label: type,
        })),
        LEAD_SOURCE_OPTIONS: settings.leadSources.map((source) => ({
            value: source,
            label: source.charAt(0).toUpperCase() + source.slice(1).replace('_', ' '),
        })),
        COST_LINE_TYPE_OPTIONS: settings.costLineTypes.map((type) => ({
            value: type.toLowerCase().replace(/\s+/g, '_'),
            label: type,
        })),
        PROJECT_COST_LINE_TYPE_OPTIONS: settings.projectCostLineTypes.map((type) => ({
            value: type.toLowerCase().replace(/\s+/g, '_'),
            label: type,
        })),
        WORK_ORDER_COST_LINE_TYPE_OPTIONS: settings.workOrderCostLineTypes.map((type) => ({
            value: type.toLowerCase().replace(/\s+/g, '_'),
            label: type,
        })),
    };

    const getDefaultValue = (key: string): unknown => {
        const setting = settings[key as keyof SettingsConstants];
        if (Array.isArray(setting) && setting.length > 0) {
            if (typeof setting[0] === 'object' && 'value' in setting[0]) {
                return setting[0].value;
            }
            return setting[0];
        }
        return null;
    };

    const getOptions = (key: string): Array<{ value: string; label: string }> => {
        const setting = settings[key as keyof SettingsConstants];
        if (Array.isArray(setting)) {
            return setting.map((item) => {
                if (typeof item === 'object' && item !== null && 'value' in item && 'label' in item) {
                    const option = item as { value: unknown; label: unknown };
                    return { value: String(option.value), label: String(option.label) };
                }
                return { value: String(item), label: String(item) };
            });
        }
        return [];
    };

    const getValueLabelPairs = (key: string): Array<{ value: string; label: string }> => {
        return getOptions(key);
    };

    const contextValue: SettingsContextType = {
        settings,
        legacy,
        isLoading,
        error,
        getDefaultValue,
        getOptions,
        getValueLabelPairs,
    };

    return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextType {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        console.error('useSettings must be used within a SettingsProvider');
        return fallbackContextValue;
    }
    return context;
}
